import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import NotFound from '@/pages/NotFound';
import { getArticleBySlug, getCategoryBySlug, getArticlesByCategory } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const article = getArticleBySlug(slug || '');
  if (!article) {
    return <NotFound />;
  }

  const category = getCategoryBySlug(article.category);
  const relatedArticles = getArticlesByCategory(article.category)
    .filter((a) => a.slug !== article.slug)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: category?.name || article.category, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://boldniche.github.io/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: category?.name || article.category,
        item: `https://boldniche.github.io/category/${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '反差设计研究所',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <Navbar />

      <main className="pt-20">
        {/* Article Header */}
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {category && (
            <Link
              to={`/category/${category.slug}`}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-5 no-underline hover:bg-primary-200 transition-colors whitespace-nowrap"
            >
              <i className={`${category.icon} text-xs`}></i>
              {category.name}
            </Link>
          )}

          <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
            {article.title}
          </h1>

          <p className="text-sm md:text-base text-foreground-500 mt-4 leading-relaxed">
            {article.excerpt}
          </p>

          <div className="flex items-center gap-3 mt-6 pb-8 border-b border-background-200/70">
            <img
              src={article.authorAvatar}
              alt={article.author}
              className="w-10 h-10 rounded-full object-cover"
            />
            <div>
              <p className="text-sm font-medium text-foreground-900">{article.author}</p>
              <p className="text-xs text-foreground-500">
                {article.date} · {article.readTime}
              </p>
            </div>
          </div>

          {/* Featured Image */}
          <div className="rounded-xl overflow-hidden my-8">
            <img
              src={article.image}
              alt={article.title}
              className="w-full aspect-[16/9] object-cover"
            />
          </div>

          {/* Article Content */}
          <div className="prose-custom">
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-8">
              {article.content.intro}
            </p>

            {article.content.sections.map((section, index) => (
              <section key={index} className="mb-8">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                  {section.heading}
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {section.body}
                </p>
              </section>
            ))}

            <blockquote className="border-l-4 border-primary-400 bg-primary-50/50 px-5 py-4 rounded-r-lg my-8">
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed italic">
                {article.content.conclusion}
              </p>
            </blockquote>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14 md:py-18">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
                {t('relatedArticles')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.slug} article={a} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}